import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { registerOnboardingLog } from '@partiu/api/onboarding/api';
import { isAuthenticated } from '@partiu/api/axios';

type OnboardingContext = 'biblioteca' | 'banca';
type OnboardingStep = 'idle' | 'meus' | 'acervo' | 'done';

function getStorageKey(context: OnboardingContext): string {
  return context === 'banca' ? 'bookstore_banca_onboarding_done' : 'bookstore_biblioteca_onboarding_done';
}

function getStoredStatus(context: OnboardingContext): boolean {
  try {
    return localStorage.getItem(getStorageKey(context)) === 'true';
  } catch {
    return false;
  }
}

function markDone(context: OnboardingContext) {
  try {
    localStorage.setItem(getStorageKey(context), 'true');
  } catch {
    // storage unavailable
  }
}

export function useBookstoreOnboarding(acervoTabIndex: number, context: OnboardingContext) {
  const [step, setStep] = useState<OnboardingStep>('idle');
  const [tabIndex, setTabIndex] = useState(0);
  const [controlled, setControlled] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) return;
    if (getStoredStatus(context)) {
      setStep('done');
      return;
    }

    const startOnboarding = () => {
      setStep('meus');
      setControlled(true);
      setTabIndex(0);
    };

    const isInsideIframe = window !== window.parent;

    if (isInsideIframe) {
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'PARTIU_SERVICOS_ONBOARDING_DONE') {
          startOnboarding();
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    } else {
      startOnboarding();
    }
  }, [context]);

  const confirmMeus = useCallback(() => {
    //registerOnboardingLog({ type: 'onboarding_produtos' }).catch(() => {});
    setStep('acervo');
    setTabIndex(acervoTabIndex);
  }, [acervoTabIndex]);

  const confirmAcervo = useCallback(() => {
    //registerOnboardingLog({ type: 'onboarding_servicos' }).catch(() => {});
    markDone(context);
    setStep('done');
    setControlled(false);
  }, [context]);

  const handleTabChange = useCallback(
    (index: number) => {
      if (step !== 'done') return;
      setTabIndex(index);
    },
    [step]
  );

  return {
    step,
    controlled,
    tabIndex,
    handleTabChange,
    confirmMeus,
    confirmAcervo,
  };
}

export function OnboardingBalloon({ text, onConfirm }: { text: string; onConfirm: () => void }) {
  return (
    <>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.600"
        zIndex={1399}
        onClick={(e) => e.stopPropagation()}
      />
      <Box position="relative" zIndex={1400} mx="5%" mt={3} mb={2}>
        <VStack
          bg="white"
          borderRadius="16px"
          padding="24px 20px"
          spacing={4}
          boxShadow="0 8px 32px rgba(0,0,0,0.18)"
          textAlign="center"
          border="2px solid"
          borderColor="blue.400"
        >
          <Box
            position="absolute"
            top="-10px"
            left="50%"
            transform="translateX(-50%)"
            w={0}
            h={0}
            borderLeft="10px solid transparent"
            borderRight="10px solid transparent"
            borderBottom="10px solid"
            borderBottomColor="blue.400"
          />
          <Text fontSize="15px" fontWeight="600" color="gray.800" lineHeight="1.5" maxW="400px">
            {text}
          </Text>
          <Button colorScheme="blue" borderRadius="10px" px={10} size="md" onClick={onConfirm}>
            OK
          </Button>
        </VStack>
      </Box>
    </>
  );
}
